import CollapsedTabs from '../../common/components/collapsedTabs';
import DocumentContainer from '../../common/components/documentViewer/documentContainer';
import ResizeHandle from '../../common/components/documentViewer/resizeHandle';
import TabsContainer from '../../common/components/documentViewer/tabsContainer';
import { ISafeExecuteAsyncAction } from '../../common/typings/stores';
import { commonTestIds } from '../../common/utils/commonTestIds';
import Page from '../page';
import OrderViewerApiService from './api/orderViewerApiService';
import DocumentIframe from './components/documentIframe';
import ViewerHeader from './components/viewerHeader';
import ViewerTabs from './components/viewerTabs';
import { ViewInLibraryStatus } from './typing/enums';
import { IOrderViewerViewModel } from './typing/orderViewerViewModel';
import { IOrderViewerMainStore } from './typing/stores';
import useInLibraryPolling from './utils/useInLibraryPolling';
import { createStyles, makeStyles } from '@mui/styles';
import { inject, observer } from 'mobx-react';
import { useState } from 'react';
import { PanelGroup } from 'react-resizable-panels';

export interface IOrderViewerPageProps {
	model?: IOrderViewerViewModel;
	api?: OrderViewerApiService;
	viewInLibraryStatus?: ViewInLibraryStatus;
	setDocumentId?: (documentId: number) => void;
	setViewInLibraryStatus?: (status: ViewInLibraryStatus) => void;
	safeExecuteAsync?: ISafeExecuteAsyncAction;
}

const useStyles = makeStyles(() =>
	createStyles({
		containder: {
			display: 'flex',
			flexDirection: 'column',
			height: '100%'
		}
	})
);

const OrderViewerPage: React.FunctionComponent<IOrderViewerPageProps> = ({ model, api, ...props }) => {
	const tabsSizeStorageKey = 'order_viewer_tabs_size';

	const setTabsSize = (size: number) => {
		localStorage.setItem(tabsSizeStorageKey, size.toString());
	};

	const getTabsSize = (): number => {
		return parseFloat(localStorage.getItem(tabsSizeStorageKey));
	};

	const classes = useStyles();
	const pollInterval = 1000 * 2;
	useInLibraryPolling(
		api,
		model.headerModel.contentId,
		pollInterval,
		props.viewInLibraryStatus,
		props.safeExecuteAsync,
		res => {
			if (res?.status === ViewInLibraryStatus.Enabled) {
				props.setDocumentId(res.documentId);
				props.setViewInLibraryStatus(res.status);
			}
		}
	);
	const [tabsCollapsed, setTabsCollapsed] = useState(false);

	return (
		<Page>
			<div className={classes.containder} data-test-id={commonTestIds.viewerHeader}>
				<ViewerHeader {...model.headerModel} />
				<PanelGroup
					direction="horizontal"
					onLayout={e => {
						const tabsPanelOrder = 1;

						if (e[tabsPanelOrder]?.sizePixels) {
							setTabsSize(e[tabsPanelOrder].sizePixels);
						}
					}}
				>
					<DocumentContainer>
						<DocumentIframe />
					</DocumentContainer>
					{!tabsCollapsed && <ResizeHandle />}
					<TabsContainer tabsCollapsed={tabsCollapsed} size={getTabsSize()}>
						<ViewerTabs onCollapse={() => setTabsCollapsed(true)} />
					</TabsContainer>
					{tabsCollapsed && (
						<CollapsedTabs title={localize('Order details')} onOpen={() => setTabsCollapsed(false)} />
					)}
				</PanelGroup>
			</div>
		</Page>
	);
};

export default inject((stores: IOrderViewerMainStore) => ({
	model: stores.orderViewerStore.viewModel,
	api: stores.api.orderViewerApiService,
	viewInLibraryStatus: stores.orderViewerStore.viewInLibraryStatus,
	setDocumentId: stores.orderViewerStore.setLibraryDocumentId,
	setViewInLibraryStatus: stores.orderViewerStore.setViewInLibraryStatus,
	safeExecuteAsync: stores.safeExecuteAsync
}))(observer(OrderViewerPage));
